/**
 * Live capacity/status controller for the venue booking wizard.
 */
(function (global) {
  'use strict';

  var UNKNOWN_CAPACITY_MAX_QTY = 1;
  var DEFAULT_POLL_INTERVAL_MS = 5000;

  function cityEvents() {
    return global.WorkshopCityEvents || null;
  }

  function maxBookableQty(slot) {
    if (!slot) return 0;
    var api = cityEvents();
    if (api && !api.isSlotBookable(slot)) return 0;
    var cap = slot.capacity_remaining;
    if (cap == null || cap === '') {
      cap = slot._raw && slot._raw.capacity_remaining;
    }
    if (cap == null || cap === '') return UNKNOWN_CAPACITY_MAX_QTY;
    var n = Number(cap);
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  function enrichSlots(slots, options) {
    var api = cityEvents();
    var list = Array.isArray(slots) ? slots : [];
    if (!api || typeof api.mergeSlotsWithLive !== 'function') return Promise.resolve(list);
    return Promise.resolve(api.mergeSlotsWithLive(list, Object.assign({ fetch: true }, options || {})));
  }

  function ensureFreshSlot(slot, options) {
    if (!slot) return Promise.resolve(null);
    return enrichSlots([slot], options).then(function (slots) {
      var fresh = slots && slots[0] ? slots[0] : slot;
      var api = cityEvents();
      if (api && !api.isSlotBookable(fresh)) return null;
      return fresh;
    });
  }

  function readPollInterval(root, config) {
    config = config || {};
    var api = cityEvents();
    if (api && typeof api.resolvePollIntervalMs === 'function') {
      var fromConfig = api.resolvePollIntervalMs('booking', null);
      if (fromConfig > 0) return fromConfig;
    }
    if (config.pollIntervalMs != null) return Number(config.pollIntervalMs) || 0;
    var attr = root && root.getAttribute && root.getAttribute('data-wbw-live-poll-interval');
    if (attr != null && attr !== '') return Number(attr) || 0;
    if (global.__WBW_LIVE_POLL_INTERVAL_MS__ != null) return Number(global.__WBW_LIVE_POLL_INTERVAL_MS__) || 0;
    return DEFAULT_POLL_INTERVAL_MS;
  }

  function liveOptions(root, config) {
    config = config || {};
    return {
      root: root,
      productHandle:
        config.productHandle ||
        (root && root.getAttribute && root.getAttribute('data-product-handle')) ||
        '',
      shopDomain:
        config.shopDomain ||
        (global.Shopify && global.Shopify.shop ? String(global.Shopify.shop) : ''),
    };
  }

  function validateSlotForCart(slot, requestedAddQty, options) {
    var api = cityEvents();
    if (!api || typeof api.validateAddToCart !== 'function') {
      return Promise.resolve({
        allowed: false,
        message: 'We could not verify workshop availability. Please try again.',
      });
    }
    var opts = Object.assign({ intent: 'add_to_cart' }, options || {});
    return api.validateAddToCart(slot, requestedAddQty, opts);
  }

  function createController(root, config) {
    config = config || {};
    var timer = null;
    var stopped = false;
    var opts = liveOptions(root, config);
    var interval = readPollInterval(root, config);

    var pollGetSlots = null;
    var pollOnUpdate = null;
    var tickInFlight = false;

    var api = cityEvents();
    if (api && typeof api.ensureBookingConfig === 'function') {
      api.ensureBookingConfig(opts).then(function () {
        if (typeof api.resolvePollIntervalMs === 'function') {
          var next = api.resolvePollIntervalMs('booking', interval);
          if (next > 0 && next !== interval) {
            interval = next;
            if (pollGetSlots) startPoll(pollGetSlots, pollOnUpdate, { immediate: false });
          }
        }
      });
    }

    function tickNow(getSlots, onUpdate) {
      if (stopped || typeof getSlots !== 'function') return Promise.resolve();
      var slots = getSlots() || [];
      if (!slots.length) return Promise.resolve();
      if (tickInFlight) return Promise.resolve();
      tickInFlight = true;
      return enrichSlots(slots, opts)
        .then(function (freshSlots) {
          if (!stopped && typeof onUpdate === 'function') {
            onUpdate(freshSlots || slots, { at: Date.now() });
          }
        })
        .catch(function () {
          /* keep polling on transient live-state failures */
        })
        .finally(function () {
          tickInFlight = false;
        });
    }

    function startPoll(getSlots, onUpdate, pollOptions) {
      pollOptions = pollOptions || {};
      var immediate = pollOptions.immediate !== false;
      stopPoll();
      pollGetSlots = getSlots;
      pollOnUpdate = onUpdate;
      if (immediate) tickNow(getSlots, onUpdate);
      if (!(interval > 0)) return null;
      timer = global.setInterval(function () {
        tickNow(getSlots, onUpdate);
      }, interval);
      return timer;
    }

    function stopPoll() {
      if (timer) {
        global.clearInterval(timer);
        timer = null;
      }
    }

    return {
      enrichSlots: function (slots) {
        return enrichSlots(slots, opts);
      },
      ensureFreshSlot: function (slot) {
        return ensureFreshSlot(slot, opts);
      },
      validateSlotForCart: function (slot, requestedAddQty) {
        return validateSlotForCart(slot, requestedAddQty, opts);
      },
      maxBookableQty: maxBookableQty,
      startPoll: startPoll,
      restartPoll: function (pollOptions) {
        if (pollGetSlots && pollOnUpdate) startPoll(pollGetSlots, pollOnUpdate, pollOptions);
      },
      tickNow: function () {
        if (pollGetSlots && pollOnUpdate) return tickNow(pollGetSlots, pollOnUpdate);
        return Promise.resolve();
      },
      stop: function () {
        stopped = true;
        stopPoll();
      },
    };
  }

  global.WorkshopBookingWizardLive = {
    createController: createController,
    enrichSlots: enrichSlots,
    ensureFreshSlot: ensureFreshSlot,
    validateSlotForCart: validateSlotForCart,
    maxBookableQty: maxBookableQty,
    startPoll: function (slots, onUpdate, options) {
      var root = options && options.root;
      return createController(root, options).startPoll(
        function () {
          return typeof slots === 'function' ? slots() : slots;
        },
        onUpdate
      );
    },
  };
})(typeof window !== 'undefined' ? window : this);
